import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { fetchIndices } from '../../../Admin/Indices/actions';
import { fetchLessons as fetch, setLessonFavorite } from '../../../Admin/Lessons/actions';
import { fetchClassRooms } from '../../../School/ClassRooms/actions';
import { fetchStudentInClassRooms } from '../../../School/StudentInClassRooms/actions';
import { getIndeices, setIndeices } from '../../../../context/sessionStorage';

export const useTrackList = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [currentItem, setCurrentItemState] = useState('');
    const [indicesState, setIndicesState] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedSkillNames, setSelectedSkillNames] = useState([]);
    const [selectedTextTypeNames, setSelectedTextTypeNames] = useState([]);
    const [selectedDimCompNames, setSelectedDimCompNames] = useState([]);
    const [selectedElementNames, setSelectedElementNames] = useState([]);
    const [activeFilterTab, setActiveFilterTab] = useState('skill');
    const [filterSearchText, setFilterSearchText] = useState('');

    const reduxIndices = useSelector(state => state.indices);
    const reduxLessons = useSelector(state => state.lessons);
    const user = useSelector(state => state.user);
    const classRooms = useSelector(state => state.classRooms);

    // Mount logic
    useEffect(() => {
        let indices = getIndeices();
        if (indices === null || indices.length === 0) {
            dispatch(fetchIndices());
        }
        setIndicesState(indices);

        const indexId = params.indexId || (props.match && props.match.params && props.match.params.indexId);
        if (indexId && indexId !== '0') {
            const parsedId = parseInt(indexId);
            setCurrentItemState({ id: parsedId });
            dispatch(fetch(parsedId));
        }

        if (window.scrollToPosition) {
            window.scrollToPosition(`main-${props.typeOfList}-section`);
        }
    }, [dispatch, params.indexId, props.match, props.typeOfList]);

    // Check classRooms fetch for student/teacher users
    useEffect(() => {
        if (user &&
            user.user &&
            user.user.UserType &&
            (user.user.UserType.Id === 3 || user.user.UserType.Id === 4) &&
            classRooms && classRooms.length === 0) {
            dispatch(fetchClassRooms());
        }
    }, [user, classRooms, dispatch]);

    const setCurrentItem = useCallback((item) => {
        setCurrentItemState(item);
        setSearch('');
        setSelectedSkillNames([]);
        setSelectedTextTypeNames([]);
        setSelectedDimCompNames([]);
        setSelectedElementNames([]);
        setActiveFilterTab('skill');
        setFilterSearchText('');
        dispatch(fetch(item.id));

        if (props.type === "BookLessons") {
            history.push(`/Tracks/BookLessons/${item.id}`);
        } else {
            history.push(`${props.Link}${item.id}`);
        }
    }, [dispatch, history, props.Link, props.type]);

    const handleSetFavorite = useCallback((id) => {
        dispatch(setLessonFavorite(id));
    }, [dispatch]);

    const handleFetchStudentInClassRooms = useCallback((ClassRoomId, MediaId) => {
        dispatch(fetchStudentInClassRooms(ClassRoomId, MediaId));
    }, [dispatch]);

    const toggleFilterItem = useCallback((category, name) => {
        const setterMap = {
            skill: setSelectedSkillNames,
            textType: setSelectedTextTypeNames,
            dimComp: setSelectedDimCompNames,
            element: setSelectedElementNames
        };
        const setter = setterMap[category];
        if (setter) {
            setter(prevList => {
                const currentList = prevList || [];
                if (currentList.includes(name)) {
                    return currentList.filter(item => item !== name);
                } else {
                    return [...currentList, name];
                }
            });
        }
    }, []);

    const clearAllFilters = useCallback(() => {
        setSelectedSkillNames([]);
        setSelectedTextTypeNames([]);
        setSelectedDimCompNames([]);
        setSelectedElementNames([]);
        setFilterSearchText('');
    }, []);

    // Indices resolution
    const effectiveIndices = useMemo(() => {
        if (reduxIndices) {
            setIndeices(reduxIndices);
            return reduxIndices;
        }
        return indicesState;
    }, [reduxIndices, indicesState]);

    // Available Category Lessons
    const currentCategoryLessons = useMemo(() => {
        if (reduxLessons && reduxLessons.lessons && currentItem) {
            return reduxLessons.lessons.filter(v => v.Index.Id === currentItem.id);
        }
        return [];
    }, [reduxLessons, currentItem]);

    // Extracted Available Filter Options
    const availableSkills = useMemo(() => {
        return Array.from(new Set(currentCategoryLessons.flatMap(l => (l.Skills || []).map(s => s.SkillName)).filter(Boolean)));
    }, [currentCategoryLessons]);

    const availableTextTypes = useMemo(() => {
        return Array.from(new Set(currentCategoryLessons.flatMap(l => (l.Skills || []).map(s => s.TextTypeName)).filter(Boolean)));
    }, [currentCategoryLessons]);

    const availableDimComps = useMemo(() => {
        return Array.from(new Set(currentCategoryLessons.flatMap(l => (l.Skills || []).map(s => s.DimensionComponentName)).filter(Boolean)));
    }, [currentCategoryLessons]);

    const availableElements = useMemo(() => {
        return Array.from(new Set(currentCategoryLessons.flatMap(l => (l.Skills || []).map(s => s.ElementName)).filter(Boolean)));
    }, [currentCategoryLessons]);

    const hasAvailableFilters = availableSkills.length > 0 || availableTextTypes.length > 0 || availableDimComps.length > 0 || availableElements.length > 0;
    const totalSelectedFilters = selectedSkillNames.length + selectedTextTypeNames.length + selectedDimCompNames.length + selectedElementNames.length;

    // Helper to traverse tree node children
    const getNode = useCallback((nodes, Id) => {
        let result = false;
        if (nodes) {
            nodes.forEach(node => {
                if (node.id === Id && node.Level >= 1) {
                    result = node.nodes;
                }
            });
            if (result) return result;
            nodes.forEach(node => {
                const subResult = getNode(node.nodes, Id);
                if (subResult) {
                    result = subResult;
                    return false;
                }
            });
        }
        return result;
    }, []);

    // Path helper
    const hasNode = useCallback((node, currentId) => {
        if (!node) return false;
        const hasSelectedNodeInChildren = (nodes, id) => {
            let res = false;
            if (nodes) {
                nodes.forEach(n => {
                    res = res || (id === n.id) || hasSelectedNodeInChildren(n.nodes, id);
                });
            }
            return res;
        };
        return (currentId === node.id) || hasSelectedNodeInChildren(node.nodes, currentId);
    }, []);

    const getNodePath = useCallback((nodes, currentId, Level, pathList) => {
        if (nodes) {
            nodes.forEach(node => {
                const isOpen = hasNode(node, currentId);
                if (isOpen) {
                    getNodePath(node.nodes, currentId, Level, pathList);
                    if (node.Level > Level) {
                        pathList.push(node);
                    }
                }
            });
        }
    }, [hasNode]);

    const getPathList = useCallback(() => {
        const pathList = [];
        getNodePath(effectiveIndices?.nodes, currentItem?.id, props.typeOfList === "Games" ? 0 : 1, pathList);
        return pathList.reverse();
    }, [effectiveIndices, currentItem, getNodePath, props.typeOfList]);

    return {
        currentItem,
        setCurrentItem,
        indices: effectiveIndices,
        search,
        setSearch,
        selectedSkillNames,
        selectedTextTypeNames,
        selectedDimCompNames,
        selectedElementNames,
        activeFilterTab,
        setActiveFilterTab,
        filterSearchText,
        setFilterSearchText,
        toggleFilterItem,
        clearAllFilters,
        handleSetFavorite,
        handleFetchStudentInClassRooms,
        availableSkills,
        availableTextTypes,
        availableDimComps,
        availableElements,
        hasAvailableFilters,
        totalSelectedFilters,
        getNode,
        getPathList,
        reduxLessons,
        user,
        classRooms
    };
};
